import React from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { OFFICER_ROLES } from "../context/AuthContext";

const ROLE_ITEMS = [["member", "Member"], ...OFFICER_ROLES.map((r) => [r, r])];
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SearchBox } from "./Pager";

const STATUSES = ["active", "pending", "inactive", "suspended", "record_only"];
const COLS = [["name", "Member"], ["member_id", "ID"], ["role", "Role"], ["status", "Status"], ["attended", "Attended"], ["percentage", "Attendance"]];
const ANY = "__any";

export function OptionSelect({ value, onChange, label, items, testId, className }) {
  return (
    <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
      <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="max-h-72"><SelectItem value={ANY}>{label}</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function DateRange({ from, to, onFrom, onTo, testPrefix }) {
  return (
    <>
      <label className="text-xs text-muted-foreground">From <Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className="h-9 w-40" data-testid={`${testPrefix}-from`} /></label>
      <label className="text-xs text-muted-foreground">To <Input type="date" value={to} onChange={(e) => onTo(e.target.value)} className="h-9 w-40" data-testid={`${testPrefix}-to`} /></label>
    </>
  );
}

export function AttendanceFilters({ f, set, onClear }) {
  const active = f.q || f.role || f.status || f.date_from || f.date_to;
  return (
    <div className="mb-3 flex flex-wrap items-end gap-2">
      <SearchBox value={f.q} onChange={(v) => set({ q: v })} placeholder="Search name or ID…" testId="attendance-search" className="w-56" />
      <OptionSelect value={f.role} onChange={(v) => set({ role: v })} label="All roles" testId="attendance-role" className="w-44" items={ROLE_ITEMS} />
      <OptionSelect value={f.status} onChange={(v) => set({ status: v })} label="All statuses" testId="attendance-status" className="w-36" items={STATUSES.map((s) => [s, s.replace("_", " ")])} />
      <DateRange from={f.date_from} to={f.date_to} onFrom={(v) => set({ date_from: v })} onTo={(v) => set({ date_to: v })} testPrefix="attendance" />
      {active && <Button size="sm" variant="ghost" onClick={onClear} data-testid="attendance-clear">Clear</Button>}
    </div>
  );
}

const SortIcon = ({ active, order }) => {
  if (!active) return <ArrowUpDown size={12} className="opacity-50" />;
  return order === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
};

function AttendanceRow({ m, onRow }) {
  return (
    <tr className="cursor-pointer border-b border-gold/15 transition-colors hover:bg-gold/10" onClick={() => onRow(m)} title="View meetings attended" data-testid={`attendance-row-${m.id}`}>
      <td className="p-3">{m.name}</td><td className="p-3 font-mono-accent">{m.member_id || "—"}</td>
      <td className="p-3 text-xs">{m.role}</td><td className="p-3 text-xs">{m.status}</td>
      <td className="p-3 font-mono-accent">{m.attended}/{m.total_meetings}</td>
      <td className="p-3 font-mono-accent">{m.percentage}%</td>
      <td className="p-3">{m.meets_threshold ? <span className="text-palm">✓ Met</span> : <span className="text-destructive">Below</span>}</td>
    </tr>
  );
}

export function AttendanceTable({ rows, sort, order, onSort, onRow }) {
  return (
    <table className="w-full text-sm" data-testid="attendance-table">
      <thead className="bg-crimson text-parchment"><tr>
        {COLS.map(([k, l]) => <th key={k} className="p-3 text-left"><button onClick={() => onSort(k)} className="flex items-center gap-1" data-testid={`attendance-sort-${k}`}>{l} <SortIcon active={sort === k} order={order} /></button></th>)}
        <th className="p-3 text-left">50% rule</th></tr></thead>
      <tbody>
        {(rows || []).map((m) => <AttendanceRow key={m.id} m={m} onRow={onRow} />)}
        {rows && rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No members match.</td></tr>}
      </tbody>
    </table>
  );
}
